import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Spinner from "../components/Spinner";
import RichTextRenderer from "../components/RichTextRenderer";
import BlockRenderer from "../components/BlockRenderer";
import SEOHead from "../components/SEOHead";
import { useSettings } from "../context/SettingsContext";
import { getPublicPath } from "../utils/seoPages";
import { ArrowLeft, ArrowRight, Clock, User, Eye, BookOpen, Package, Sparkles } from "lucide-react";
import { useSetHelpMessage } from "../context/HelpContext";

export default function BlogPostDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const { settings: s } = useSettings();
  const blogPath = getPublicPath(s, "/blog");
  const [related, setRelated] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => navigate(blogPath))
      .finally(() => setLoading(false));
    api.get("/blog/posts?limit=20").then((r) => setRelated(r.data)).catch(() => {});
  }, [slug, navigate]);  // eslint-disable-line react-hooks/exhaustive-deps

  useSetHelpMessage(
    post ? `Bonjour ! J'ai lu votre article "${post.title}" et j'ai une question.` : null,
    [post?.title]
  );

  if (loading) return <Spinner className="min-h-[60vh]" />;
  if (!post) return null;

  const otherPosts = related.filter((p) => p.slug !== slug).slice(0, 3);
  const postImageAlt = post.image_alt || post.seo_subtitle || post.seo_title || post.title || "Image article SF Learning";
  const postDescription = post.seo_description || post.seo_subtitle || post.excerpt;

  return (
    <article className="bg-white" data-testid="blog-post-page">
      <SEOHead
        title={post.seo_title || `${post.title} — SF Learning`}
        description={postDescription}
        image={post.cover_image}
        imageAlt={postImageAlt}
        url={typeof window !== "undefined" ? window.location.href : ""}
        type="article"
        publishedTime={post.created_at || post.published_at}
        modifiedTime={post.updated_at}
        author="SF Learning"
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-4">
        <Link to={blogPath} className="text-sm text-gray-500 inline-flex items-center gap-1 mb-6 hover:text-brand-green" data-testid="back-to-blog">
          <ArrowLeft className="w-4 h-4" /> Retour au blog
        </Link>
        <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight leading-tight">{post.title}</h1>
        {post.seo_subtitle && <p className="text-xl font-semibold text-brand-green mt-4">{post.seo_subtitle}</p>}
        <p className="text-lg text-gray-600 mt-4">{post.excerpt}</p>
        <div className="flex items-center gap-4 mt-6 text-sm text-gray-500">
          <span className="inline-flex items-center gap-1"><User className="w-4 h-4" /> {post.author_name || "SF Learning"}</span>
          {post.published_at && <span className="inline-flex items-center gap-1"><Clock className="w-4 h-4" /> {new Date(post.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span>}
          <span className="inline-flex items-center gap-1"><Eye className="w-4 h-4" /> {post.view_count || 0} vues</span>
        </div>
      </div>

      {post.cover_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="w-full rounded-2xl overflow-hidden bg-black/5 border border-gray-100">
            <img src={post.cover_image} alt={postImageAlt} className="w-full h-auto object-contain block" />
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <BlockRenderer blocks={post.blocks || []} />

        {/* CTA card */}
        {post.cta && (
          <div className="my-12 card p-6 md:p-8 bg-gradient-to-br from-emerald-50 to-amber-50 relative overflow-hidden" data-testid="blog-cta-card">
            <div className="absolute right-[-60px] top-[-60px] w-[200px] h-[200px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(244,161,0,0.3), transparent 70%)" }} />
            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
              {post.cta.cover_image && (
                <div className="md:col-span-1">
                  <img src={post.cta.cover_image} alt={post.cta.title || "Ressource recommandée SF Learning"} className="rounded-xl aspect-video w-full object-cover" />
                </div>
              )}
              <div className="md:col-span-2">
                <div className="flex items-center gap-2 mb-2">
                  <span className="badge orange"><Sparkles className="w-3 h-3" /> {post.cta_text || "Va plus loin"}</span>
                </div>
                <h3 className="font-heading text-2xl font-black mb-2">
                  {post.cta.type === "pack" ? <Package className="inline w-6 h-6 mr-2 text-brand-orange" /> : <BookOpen className="inline w-6 h-6 mr-2 text-brand-green" />}
                  {post.cta.title}
                </h3>
                <RichTextRenderer html={post.cta.short_description} className="text-sm text-gray-700 mb-4" />
                <div className="flex items-center gap-4">
                  <p className="font-heading text-2xl font-bold text-brand-green">{post.cta.price_eur}€</p>
                  <Link
                    to={post.cta.type === "pack" ? `/packs/${post.cta.pack_id}` : `/formations/${post.cta.slug || post.cta.course_id}`}
                    className="btn-primary"
                    data-testid="blog-cta-btn"
                  >
                    Découvrir <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Related */}
        {otherPosts.length > 0 && (
          <div className="mt-16">
            <h3 className="font-heading text-2xl font-bold mb-6 text-brand-green">À lire aussi</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {otherPosts.map((p) => (
                <Link key={p.post_id} to={`/blog/${p.slug}`} className="card overflow-hidden group" data-testid={`related-${p.slug}`}>
                  {p.cover_image && <div className="aspect-video bg-emerald-50 overflow-hidden"><img src={p.cover_image} alt={p.seo_subtitle || p.title || "Article SF Learning"} className="w-full h-full object-contain group-hover:scale-105 transition-transform" /></div>}
                  <div className="p-4">
                    <p className="font-heading font-bold line-clamp-2 group-hover:text-brand-green">{p.title}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
